import { CheckCircle2, Signal } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { getStatus } from "@/features/eservices/api";
import { ConnectDialog } from "@/features/eservices/components/connect-dialog";
import type { ConnectionState } from "@/features/eservices/types";
import { HubStepShell } from "../hub/hub-step-shell";
import type { OnboardingStepProps } from "./types";

/** Step 5 — connect IRS e-Services using the existing phased connect flow. The
 * step is a thin wrapper: it surfaces connection status and opens `ConnectDialog`;
 * "Continue" simply advances. */
export function EServicesStep({ meta, index, isFirst, onBack, onSkip, onSaved }: OnboardingStepProps) {
  const [state, setState] = useState<ConnectionState | null>(null);
  const [connectOpen, setConnectOpen] = useState(false);

  function refreshStatus() {
    getStatus()
      .then((s) => setState(s.state))
      .catch(() => setState(null));
  }

  useEffect(refreshStatus, []);

  const connected = state === "CONNECTED";

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        onSaved();
      }}
    >
      <HubStepShell
        meta={meta}
        index={index}
        onBack={onBack}
        onSkip={onSkip}
        isFirst={isFirst}
        submitting={false}
        continueLabel="Continue"
      >
        {connected ? (
          <Alert className="border-success/40 bg-success-soft">
            <CheckCircle2 className="text-success size-4" />
            <AlertTitle>Connected to IRS e-Services</AlertTitle>
            <AlertDescription>
              You can pull transcripts and monitor CAF status from the e-Services hub.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert>
            <Signal className="size-4" />
            <AlertTitle>Not connected yet</AlertTitle>
            <AlertDescription>
              Connect with a CAF-holding practitioner's e-Services login to pull transcripts and
              monitor CAF status. The IRS login is completed by the connector.
            </AlertDescription>
          </Alert>
        )}

        <div>
          <Button type="button" variant={connected ? "outline" : "default"} onClick={() => setConnectOpen(true)}>
            <Signal />
            {connected ? "Manage connection" : "Connect to IRS e-Services"}
          </Button>
        </div>
      </HubStepShell>

      <ConnectDialog open={connectOpen} onOpenChange={setConnectOpen} onConnected={refreshStatus} />
    </form>
  );
}
